/**
 * The oracle's badge where maps are picked: the lobby's kitchen picker and the
 * admin's kitchen table.
 *
 * Both read `GET /api/oracle` — the cache, never a solve — *after* their own
 * data has rendered, so neither page ever waits on it: a picker with no
 * suffix is a picker that works, and a fetch that fails just leaves the
 * suffix off. The landing page calls the oracle's score "best score" and marks one
 * the safety abort cut short "(partial)"; the admin table keeps the budget
 * label ("full search", "truncated").
 *
 * Pure apart from `fetchOracleList`; the components hold the state.
 */

import type { OracleListEntry, OracleStatus } from '@overcooked/shared'
import { points } from '../oracle/summary'

/** 142 -> "142", −10 -> "−10" (a real minus sign). */
const amount = (n: number): string => (n < 0 ? `−${Math.abs(n)}` : String(n))

/**
 * What the landing picker appends to a kitchen's option, after a " · ":
 * "best score 142", "best score 142 (partial)" when the safety abort cut the
 * solve short, "best score: working…" while it is being solved, and nothing when there is no row
 * (not loaded yet, or the fetch failed), the solve failed, or no dish can be
 * served there.
 */
export function lobbyOracleSuffix(entry: OracleListEntry | undefined): string {
  if (!entry) return ''
  switch (entry.status) {
    case 'ready':
      if (typeof entry.score !== 'number') return ''
      return `best score ${amount(entry.score)}${entry.truncated ? ' (partial)' : ''}`
    case 'solving':
      return 'best score: working…'
    case 'impossible':
    case 'failed':
      return ''
  }
}

/** One admin table cell: `data-status`, the words, and the budget under them. */
export interface AdminOracleCell {
  /** `data-status`: an `OracleStatus`, or `loading` before the list arrives. */
  status: OracleStatus | 'loading'
  text: string
  /** "full search (4000 expansions)", with ", truncated" when it was cut short. Empty when there is no number. */
  label: string
  /** Whether there is a round to watch on `/oracle/<map>`. */
  watchable: boolean
}

export function adminOracleCell(entry: OracleListEntry | undefined): AdminOracleCell {
  if (!entry) return { status: 'loading', text: '…', label: '', watchable: false }
  switch (entry.status) {
    case 'ready':
      return {
        status: 'ready',
        text: typeof entry.score === 'number' ? points(entry.score) : 'ready',
        label: `${entry.budgetLabel}${entry.truncated ? ', truncated' : ''}`,
        watchable: true,
      }
    case 'solving':
      return { status: 'solving', text: 'solving…', label: entry.budgetLabel, watchable: false }
    case 'impossible':
      return { status: 'impossible', text: 'Impossible: no dish can be carried to the hatch', label: '', watchable: true }
    case 'failed':
      return { status: 'failed', text: 'The solve failed', label: '', watchable: false }
  }
}

/** The list by map name. */
export function oracleByMap(rows: readonly OracleListEntry[] | null): Map<string, OracleListEntry> {
  return new Map((rows ?? []).map((row) => [row.map, row]))
}

/** Whether any row is still being solved — worth asking again in a while. */
export const anySolving = (rows: readonly OracleListEntry[] | null): boolean =>
  (rows ?? []).some((row) => row.status === 'solving')

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>

/** `GET /api/oracle`, or `null` when it could not be read. Never throws. */
export async function fetchOracleList(fetcher: Fetcher = fetch): Promise<OracleListEntry[] | null> {
  try {
    const response = await fetcher('/api/oracle')
    if (!response.ok) return null
    const rows = (await response.json()) as unknown
    return Array.isArray(rows) ? (rows as OracleListEntry[]) : null
  } catch {
    return null
  }
}
